import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import * as routes from 'api';
import { deleteJSON, fetchJSON, postJSON, putJSON } from 'api/fetch';
import { pollWorkerStatus } from 'api/workerStatus';
import { captureException } from 'fe-core/util/errorTracking';
import { RootState } from 'initializers/types';

import {
  openSchedulingInsightsModal,
  showNotification,
} from 'features/scheduleBuilder/ScheduleBuilderView/slice';

import * as flashNotice from 'util/flashNotice';
import { hideGlobalLoader } from 'util/globalLoader';
import { toI18n } from 'util/i18n';

import { selectAllDepartments } from '../departments/slice';
import { captureHydrationException, selectSliceAttribute } from '../helpers';
import { reloadSchedule } from '../schedule/actions';
import { fetchSession } from '../session/slice';

import { SLICE_NAME } from './constants';
import type {
  ApplyTemplatePayload,
  ApplyTemplateResponse,
  FormattedTemplate,
  RepeatTemplatePayload,
  Template,
  TemplateSerializedEntity,
  TemplatesState,
} from './types';

export const templatesAdapter = createEntityAdapter<Template>();

export const initialState: TemplatesState = {
  ...templatesAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const getScheduleTemplateState = (state: RootState) =>
  state.get('scheduleBuilderSchedule');

const handleSuccess = () => {
  hideGlobalLoader();
  flashNotice.success(toI18n('schedule_builder.templates.apply_success'));
};

export const initializeCreateFromTemplate = createAsyncThunk(
  `${SLICE_NAME}/initializeCreateFromTemplate`,
  async ({
    templateId,
    date,
    createEvent,
  }: {
    templateId: string;
    date: any;
    createEvent: boolean;
  }) => {
    try {
      const response = await postJSON(
        routes.createFromTemplateRoute(templateId),
        {
          body: JSON.stringify({
            template_id: templateId,
            date: date.format('MM/DD/YYYY'),
          }),
        }
      );
      return { ...response, templateId, createEvent };
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const renameTemplate = createAsyncThunk(
  `${SLICE_NAME}/renameTemplate`,
  async ({ templateId, name }: { templateId: string; name: string }) => {
    try {
      const response = await putJSON(routes.scheduleTemplateRoute(templateId), {
        body: JSON.stringify({
          template: { name },
        }),
      });
      return { response, templateId, name };
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const createTemplate = createAsyncThunk(
  `${SLICE_NAME}/createTemplate`,
  async ({
    date,
    successMessage,
    repeat,
    ...params
  }: {
    date: { start_date: string; end_date: string };
    successMessage?: string;
    repeat?: boolean;
    [key: string]: any;
  }) => {
    try {
      const response = await postJSON(routes.scheduleTemplatesRoute(), {
        data: {
          attributes: params,
        },
        meta: date,
      });

      if (successMessage) {
        flashNotice.success(successMessage);
      }

      return response;
    } catch (error) {
      hideGlobalLoader();
      throw error;
    }
  }
);

export const fetchTemplates = createAsyncThunk(
  `${SLICE_NAME}/fetchTemplates`,
  async () => {
    try {
      const response = await fetchJSON(routes.scheduleTemplatesRoute());
      return response;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const updateTemplate = createAsyncThunk(
  `${SLICE_NAME}/updateTemplate`,
  async ({ templateId, name }: { templateId: string; name: string }) => {
    try {
      const response = await putJSON(routes.scheduleTemplateRoute(templateId), {
        data: {
          attributes: {
            name,
          },
        },
      });
      return response.data;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  `${SLICE_NAME}/deleteTemplate`,
  async (templateId: string) => {
    try {
      await deleteJSON(routes.scheduleTemplateRoute(templateId));
      return templateId;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const applyTemplate = createAsyncThunk(
  `${SLICE_NAME}/apply`,
  async (
    { id, payload }: { id: string; payload: ApplyTemplatePayload },
    { dispatch, getState }
  ) => {
    try {
      const response = await postJSON(
        `/schedule_builder/templates/${id}/apply`,
        payload
      );
      const jobId = response.data?.id;
      const state = getState();

      const isScheduleInsightsModalDismissed = selectSliceAttribute({
        state,
        attribute: 'isScheduleInsightsModalDismissed',
        sliceName: 'schedule',
      });

      if (jobId) {
        await pollWorkerStatus(jobId)
          .then((resp: ApplyTemplateResponse) => {
            if (resp.result === 'true') {
              handleSuccess();
              if (!isScheduleInsightsModalDismissed) {
                setTimeout(() => {
                  dispatch(openSchedulingInsightsModal());
                }, 5000);
              }
            } else {
              captureException(toI18n('errors.generic'));
            }
          })
          .catch((error: any) => {
            captureException(error);
          });
      }

      await dispatch(
        reloadSchedule({
          startDate: payload.start_date,
          endDate: payload.end_date,
        })
      );
      return response;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

export const repeatTemplate = createAsyncThunk(
  `${SLICE_NAME}/repeat`,
  async (payload: RepeatTemplatePayload) => {
    try {
      const response = await postJSON('/schedule_builder/templates/repeat', {
        ...payload,
      });
      return response;
    } catch (error: unknown) {
      showNotification({
        message: toI18n('errors.generic'),
        type: 'error',
      });
      captureException(error);
    }
  }
);

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchSession.fulfilled, (state: any, action: any) => {
      state.status = 'succeeded';
      if (action.payload?.included) {
        try {
          const templates = action.payload.included
            .filter(
              (entity: TemplateSerializedEntity) =>
                entity.type === 'scheduleTemplate'
            )
            .map((entity: TemplateSerializedEntity) => ({
              id: entity.id,
              type: entity.type,
              name: entity.attributes.name,
              shiftsCount: entity.attributes.shiftsCount,
              totalHours: entity.attributes.totalHours,
              uniqueDepartmentsCount: entity.attributes.uniqueDepartmentsCount,
              updatedByName: entity.attributes.updatedByName,
              createdAt: entity.attributes.createdAt,
              updatedAt: entity.attributes.updatedAt,
              ...Object.fromEntries(
                Object.entries(entity.relationships || {}).map(
                  ([k, v]: [string, any]) => [k, v.data]
                )
              ),
            }));
          templatesAdapter.setAll(state, templates);
        } catch (error) {
          captureHydrationException(state, error, SLICE_NAME);
        }
      }
    });
    builder.addCase(applyTemplate.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(applyTemplate.fulfilled, state => {
      state.status = 'succeeded';
    });
    builder.addCase(applyTemplate.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to apply template';
    });
    builder.addCase(repeatTemplate.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(repeatTemplate.fulfilled, (state, action) => {
      if (action.payload?.data === 'ok') {
        state.status = 'succeeded';
      }
    });
    builder.addCase(repeatTemplate.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to repeat template';
    });
    builder.addCase(initializeCreateFromTemplate.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(initializeCreateFromTemplate.fulfilled, state => {
      state.status = 'succeeded';
    });
    builder.addCase(initializeCreateFromTemplate.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to create from template';
    });

    // Handle fetchTemplates thunk states
    builder.addCase(fetchTemplates.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(fetchTemplates.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action.payload && Array.isArray(action.payload)) {
        templatesAdapter.setAll(state, action.payload);
      }
    });
    builder.addCase(fetchTemplates.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch templates';
    });
    builder.addCase(createTemplate.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(createTemplate.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action.payload?.data) {
        const templateData = action.payload.data;
        const template: Template = {
          id: templateData.id,
          name: templateData.attributes.name,
          shiftsCount: templateData.attributes.shiftsCount,
          totalHours: templateData.attributes.totalHours,
          uniqueDepartmentsCount:
            templateData.attributes.uniqueDepartmentsCount,
          updatedByName: templateData.attributes.updatedByName,
          createdAt: templateData.attributes.createdAt,
          updatedAt: templateData.attributes.updatedAt,
        };

        templatesAdapter.addOne(state, template);
      }
    });
    builder.addCase(createTemplate.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to create template';
    });
    builder.addCase(deleteTemplate.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(deleteTemplate.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action?.payload) {
        templatesAdapter.removeOne(state, action.payload);
      }
    });
    builder.addCase(deleteTemplate.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to delete template';
    });
    builder.addCase(renameTemplate.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(renameTemplate.fulfilled, (state, action) => {
      state.status = 'succeeded';
      if (action?.payload) {
        const { templateId, name } = action.payload;
        templatesAdapter.updateOne(state, {
          id: templateId,
          changes: { name },
        });
      }
    });
    builder.addCase(renameTemplate.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to rename template';
    });
    builder.addCase(updateTemplate.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(updateTemplate.fulfilled, (state, action) => {
      state.status = 'succeeded';
      const updatedTemplate = action.payload;
      templatesAdapter.updateOne(state, {
        id: updatedTemplate.id,
        changes: {
          name: updatedTemplate.attributes.name,
          updatedAt: updatedTemplate.attributes.updatedAt,
          updatedByName: updatedTemplate.attributes.updatedByName,
        },
      });
    });
    builder.addCase(updateTemplate.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to update template';
    });
  },
});

export const reducer = slice.reducer;

export const {
  selectById: selectTemplateById,
  selectIds: selectTemplateIds,
  selectEntities: selectTemplateEntities,
  selectAll: selectAllTemplates,
  selectTotal: selectTotalTemplates,
} = templatesAdapter.getSelectors(
  (state: any) => getScheduleTemplateState(state).templates
);

export const selectFormattedTemplates = createSelector(
  [selectAllTemplates, (state: any) => selectAllDepartments(state).length],
  (templates: Template[], departmentsCount: number): FormattedTemplate[] =>
    templates.reduce<FormattedTemplate[]>((memo, template) => {
      memo.unshift({
        id: template.id,
        lastModified: template.updatedAt,
        totalShifts: template.shiftsCount,
        totalHours: template.totalHours,
        username: template.updatedByName,
        title: template.name,
        departments: toI18n(
          'schedule_builder.templates_drawer.departments_stats',
          {
            props: {
              unique_departments: template.uniqueDepartmentsCount,
              total_departments: departmentsCount,
            },
          }
        ),
      });
      return memo;
    }, [])
);

export const selectTemplatesIsLoading = (state: RootState) =>
  getScheduleTemplateState(state).templates.status === 'pending';
